import { Flex } from 'components/Flex';
import { IExecutionProgressTracking } from 'types/executionProgressTrackings';
import {
  IExecutionProgressStatus,
  IExecutionProgressStatusAttribute
} from 'types/executionProgressStatuses';
import { PDCAPie } from 'components/ExecutionProgressTracking/components/PDCAPie';
import { useState } from 'react';
import { ConfirmModal } from './components/ConfirmModal';
import { useAccount } from 'hooks/useAccount';
import { ExecutionProgressStatusInfo } from './components/ExecutionProgressStatusInfo';
import { IUser } from 'types/user';
import { FieldLabel } from 'components/_form';
import { DMAIC } from './components/DMAIC';
import { Tooltip } from 'react-tooltip';
import { fullName } from 'utilities/fullName';
import { P } from 'components/Typography/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { dateFormat } from 'constants/dateFormats/dateFormat';
import { findLastExecutionProgressTrackingLetter } from 'utilities/lastExecutionProgressTrackingLetter';

interface Props {
  id?: string;
  executionProgressTracking?: IExecutionProgressTracking;
  activeAll?: boolean;
  executionProgressStatuses?: (
    | IExecutionProgressStatusAttribute
    | IExecutionProgressStatus
  )[];
  onChange?: (
    kind: (IExecutionProgressStatusAttribute | IExecutionProgressStatus)[]
  ) => void;
  usersList?: IUser[];
  label?: string | null;
  showStatusInfo?: boolean;
  onlyRealization?: boolean;
}

export const ExecutionProgressTracking: React.FC<Props> = ({
  id,
  activeAll,
  executionProgressTracking,
  executionProgressStatuses,
  onChange,
  usersList,
  label,
  showStatusInfo,
  onlyRealization
}) => {
  if (!executionProgressTracking) {
    return <></>;
  }

  const [chosenKind, setChosenKind] = useState<string>();
  const [tooltipData, setTooltipData] = useState<{
    user: string;
    date: string;
  }>();

  const { loggedUserData } = useAccount();

  const activeLetters = executionProgressStatuses?.map(
    (executionProgressStatus) => executionProgressStatus.kind
  );

  const lastExecutionProgressTrackingLetter = findLastExecutionProgressTrackingLetter(
    executionProgressTracking,
    executionProgressStatuses || []
  );

  const letters = executionProgressTracking.kind.split('');

  const availableLetter =
    letters[
      (letters.findIndex((val) => val === lastExecutionProgressTrackingLetter) || 0) +
        1
    ];

  const minDate =
    chosenKind && !activeLetters?.includes(chosenKind) ? new Date() : undefined;

  const handleClickKind = (kind: string) => {
    onChange && setChosenKind(kind);
  };

  const handleSubmit = (date: string) => {
    if (chosenKind) {
      onChange?.([
        ...(executionProgressStatuses || []),
        {
          kind: chosenKind,
          confirmed_at: date,
          execution_progress_tracking_id: executionProgressTracking.id,
          approver_id: Number(loggedUserData.id)
        },
        ...(onlyRealization && chosenKind === 'C'
          ? [
              {
                kind: 'A',
                confirmed_at: date,
                execution_progress_tracking_id: executionProgressTracking.id,
                approver_id: Number(loggedUserData.id)
              }
            ]
          : [])
      ]);

      setChosenKind(undefined);
    }
  };

  const handleRemoveExecutionProgressStatus = (kindToRemove: string) => {
    onChange?.(
      (executionProgressStatuses || []).filter(
        ({ kind }) => kind !== kindToRemove
      )
    );
  };

  const onLetterHover = (letter: string) => {
    const executionProgressStatus = executionProgressStatuses?.find(
      ({ kind }) => kind === letter
    );

    const user = usersList?.find(
      ({ id }) => id === executionProgressStatus?.approver_id
    );

    if (executionProgressStatus && user) {
      setTooltipData({
        user: fullName(user?.profile),
        date: executionProgressStatus?.confirmed_at || ''
      });
    }
  };

  const onLetterMouseLeave = () => {
    setTooltipData(undefined);
  };

  return (
    <Flex
      width="100%"
      height="100%"
      alignItems="center"
      gap="12px"
      flexDirection={['column', 'row']}
    >
      <Flex
        id={`${id}-ExecutionProgressTracking`}
        flexDirection="column"
        alignItems={executionProgressTracking.kind === 'PDCA' ? 'center' : ''}
      >
        {label && <FieldLabel width="fit-content">{label}</FieldLabel>}
        {executionProgressTracking.kind === 'PDCA' ? (
          <PDCAPie
            activeAll={activeAll}
            activeLetters={activeLetters}
            handleClickKind={handleClickKind}
            onLetterHover={onLetterHover}
            onLetterMouseLeave={onLetterMouseLeave}
            availableLetter={availableLetter}
          />
        ) : (
          <DMAIC
            activeAll={activeAll}
            activeLetters={activeLetters}
            handleClickKind={handleClickKind}
            executionProgressTracking={executionProgressTracking}
            onLetterHover={onLetterHover}
            onLetterMouseLeave={onLetterMouseLeave}
            availableLetter={availableLetter}
          />
        )}
      </Flex>

      {showStatusInfo && (
        <ExecutionProgressStatusInfo
          executionProgressTracking={executionProgressTracking}
          executionProgressStatuses={executionProgressStatuses}
          usersList={usersList}
          handleRemoveExecutionProgressStatus={handleRemoveExecutionProgressStatus}
        />
      )}

      <ConfirmModal
        isOpen={!!chosenKind}
        onCancelClick={() => {
          setChosenKind(undefined);
        }}
        handleSubmit={handleSubmit}
        minDate={minDate}
      />

      <Tooltip anchorId={`${id}-ExecutionProgressTracking`} isOpen={!!tooltipData}>
        <Flex gap="5px" alignItems="center">
          <FontAwesomeIcon icon={faClock} />
          <P variant="body">
            {tooltipData?.date &&
              format(new Date(tooltipData?.date), dateFormat)}
          </P>
        </Flex>
        <Flex gap="5px" alignItems="center">
          <FontAwesomeIcon icon={faUser} />
          <P variant="body">{tooltipData?.user}</P>
        </Flex>
      </Tooltip>
    </Flex>
  );
};
